import { CheckIcon, ChevronDownIcon, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useState, type FC } from "react";
import Text from "../Text";
import { useTranslation } from "react-i18next";
import { setActionTriggerNeedCore } from "@/store/restrictedFeatureState";
import { cn } from "@/lib/utils";

type OptionsSelectMultiple = { label: string; value: string }[];

type Props = {
  onChange: (value: string[]) => void;
  isRestricted: boolean;
  value: string[];
  options: OptionsSelectMultiple;
  placeholder?: string;
  className?: string;
};

const SelectMultiple: FC<Props> = ({
  onChange,
  value,
  isRestricted,
  options,
  placeholder,
  className,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="secondaryFilled"
          role="combobox"
          aria-expanded={open}
          className={cn("justify-between", className)}
          onClick={() => {
            if (isRestricted) {
              setActionTriggerNeedCore(true);
            }
          }}
        >
          <Text className="truncate" color="black">
            {value.length > 0
              ? value
                  .map((v) =>
                    t(options.find((c) => c.value === v)?.label || ""),
                  )
                  .join(", ")
              : placeholder}
          </Text>

          {isRestricted ? (
            <Lock
              width={24}
              height={24}
              color="#535862"
              className="ml-2 h-4 w-4 shrink-0"
            />
          ) : (
            <ChevronDownIcon
              width={24}
              height={24}
              color="#758195"
              className="ml-2 h-4 w-4 shrink-0"
              style={{
                transition: "transform 0.3s",
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          )}
        </Button>
      </PopoverTrigger>
      {!isRestricted && (
        <PopoverContent className="p-[12px]">
          <div className="flex flex-col gap-[8px] my-[8px] h-[300px] overflow-y-auto">
            {options.map((option, index) => (
              <button
                key={`value-${option.value}-${index}`}
                onClick={() => {
                  if (value.includes(option.value)) {
                    onChange(value.filter((v) => v !== option.value));
                  } else {
                    onChange([...value, option.value]);
                  }
                }}
                className="flex items-center gap-[8px] px-[12px] py-[8px] hover:bg-[#F1F2F4]"
              >
                {value.includes(option.value) && (
                  <CheckIcon height={16} width={16} />
                )}
                <Text fontSize={16} fontWeight={400}>
                  {t(option.label)}
                </Text>
              </button>
            ))}
          </div>
        </PopoverContent>
      )}
    </Popover>
  );
};

export default SelectMultiple;
